import React, { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';

const glitch = keyframes`
  0% { transform: translate(0); }
  20% { transform: translate(-2px, 2px); }
  40% { transform: translate(-2px, -2px); }
  60% { transform: translate(2px, 2px); }
  80% { transform: translate(2px, -2px); }
  100% { transform: translate(0); }
`;

const glowAnimation = keyframes`
  0% { text-shadow: 0 0 10px #00ff00; }
  50% { text-shadow: 0 0 20px #00ff00, 0 0 30px #00ff00; }
  100% { text-shadow: 0 0 10px #00ff00; }
`;

const PageContainer = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: center;
  height: clamp(50vh, 60vh, 75vh);
  overflow: hidden;
`;

const NavContainer = styled.div`
  width: clamp(300px, 90%, 600px);
  padding: clamp(0.5rem, 3vw, 1rem);
  color: #00ff00;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  font-family: 'Courier New', monospace;
  text-align: center;
  background-color: rgba(0, 0, 0, 0.7); // Semi-transparent background
  border-radius: 10px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  z-index: 1; // Ensure it's above the canvas
`;

const Title = styled.h1`
  font-size: clamp(2rem, 10vw, 5rem);
  color: #00ff00;
  margin: 0 0 clamp(0.5rem, 2vw, 1rem) 0;
  transition: transform 0.3s;
  text-align: center;

  &:hover {
    transform: scale(1.1);
  }

  @media (max-width: 480px) {
    &:hover {
      transform: scale(1.05);
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: clamp(0.5rem, 2vw, 1rem);
  justify-content: center;
  
  @media (max-width: 480px) {
    width: 100%;
  }
`;

const NavButton = styled(Link)`
  padding: clamp(0.4rem, 2vw, 0.5rem) clamp(1rem, 4vw, 2rem);
  font-size: clamp(0.9rem, 2.5vw, 1rem);
  background: transparent;
  color: #00ff00;
  border: 2px solid #00ff00;
  cursor: pointer;
  text-decoration: none;
  transition: background-color 0.3s, color 0.3s;

  &:hover {
    background-color: #00ff00;
    color: black;
  }

  @media (max-width: 480px) {
    min-width: 100px;
    text-align: center;
  }
`;

const Links = styled.div`
  display: flex;
  gap: clamp(0.5rem, 3vw, 1rem);
  margin-top: clamp(0.5rem, 2vw, 1rem);
  background-color: transparent;

  img {
    width: clamp(35px, 10vw, 50px);
    height: clamp(35px, 10vw, 50px);
  }
`;

const Button = styled.button`
  background: transparent;
  padding: 0;
  border: none;
  
  a {
    padding: clamp(8px, 3vw, 10px) clamp(15px, 5vw, 20px);
    font-size: clamp(14px, 2.5vw, 16px);
    background-color: #333;
    color: green;
    border: none;
    border-radius: 5px;
    cursor: pointer;
    text-decoration: none;
    display: inline-block;
    
    &:hover {
      background-color: #444;
    }

    @media (max-width: 480px) {
      display: block;
      width: 100%;
      text-align: center;
    }
  }
`;

const NavHeader = ({ title }) => {
  const [displayedTitle, setDisplayedTitle] = React.useState('');
  const [showCursor, setShowCursor] = React.useState(true);
  const navRef = useRef(null); // Reference for the nav header

  React.useEffect(() => {
    let blinkCount = 0;
    const blinkTimer = setInterval(() => {
      setShowCursor(prev => !prev);
      blinkCount++;
      if (blinkCount >= 1) {
        clearInterval(blinkTimer);
        startTyping();
      }
    }, 500);

    const startTyping = () => {
      let titleIndex = 0;
      const titleTimer = setInterval(() => {
        if (titleIndex <= title.length) {
          setDisplayedTitle(title.slice(0, titleIndex));
          titleIndex++;
        } else {
          clearInterval(titleTimer);
        }
      }, 100);
    };

    return () => clearInterval(blinkTimer);
  }, [title]);

  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('animate-fade-in'); // Add animation class
          observer.unobserve(entry.target); // Stop observing after animation
        }
      });
    });

    if (navRef.current) {
      observer.observe(navRef.current); // Observe the nav header
    }

    return () => {
      if (navRef.current) {
        observer.unobserve(navRef.current); // Clean up observer
      }
    };
  }, []);

  return (
    <PageContainer ref={navRef}>
      <NavContainer>
        <Title>
          {displayedTitle}
          {displayedTitle.length < title.length && showCursor ? '_' : ''}
        </Title>
        <Button>
          <a
            href="resume.pdf"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              background: 'transparent',
              color: '#00ff00',
              border: '2px solid #00ff00',
              padding: '0.5rem 2rem',
              cursor: 'pointer',
              textDecoration: 'none',
              transition: 'background-color 0.3s, color 0.3s',
              '&:hover': {
                backgroundColor: '#00ff00',
                color: 'black'
              }
            }}
          >
          Download Resume
          </a>
        </Button>
        
        <br></br>
        <Links>
          <a href="https://www.linkedin.com/in/jack-shkifati/" target="_blank">
            <img border="0" alt="linked" src="linkedin2.png" width="50" height="50" ></img>
         </a>
          <a href="https://github.com/JackShkifati28" target="_blank">
            <img border="0" alt="Git" src="git.png" width="50" height="50"></img>
          </a>
         </Links>
         <br></br>
        <ButtonContainer>
          <NavButton to="/">Home</NavButton>
          <NavButton to="/projects">Projects</NavButton>
        </ButtonContainer>
      </NavContainer>
    </PageContainer>
  );
};

export default NavHeader;